import * as React from "react";
import * as React44ms from "../../Util/form-utils/react-44ms";

import {
  ACountry,
  AddressAutocompleteFormLogic,
  AnyNameFormLogic,
  AutocompleteInput2023,
  AutocompleteInputFormController,
  CheckboxInput2023Expects,
  ManualAddressFormLogic,
  MobileFormLogic2023,
  SelectWithDropdownInput_Option,
  SingleLineTextInput2023,
  SingleLineTextInput2023ViewModel,
  useDataSourceCheckoutRegionsQuery,
  useCustomerInformationQuery,
} from "../../Common";

import {
  useDataMutableSourceShippingSessionStorageData,
  useDataMutableSourceShippingSessionStorageData_asController,
  useDeliveryDetailsUserExperience_asController,
  useMyBagDataSourceIntersection,
} from "../providers";

import {
  BillingAddressFormExpects,
  SavedAddressExpects,
  ShippingAddressFormExpects,
} from "../model";

import { SavedAddressFormLogic } from "../forms";

import { ReactCheckout2022_AddressFormTypes } from "./delivery-details-address-form-types";
import {
  ManualAddressFormFragment,
  ManualAddressFormFragmentProps,
} from "./delivery-details-manual-address-form-fragment";
import {
  SavedAddressFormFragment,
  SavedAddressFormFragmentProps,
} from "./delivery-details-saved-address-form-fragment";
import { useMyPreviouslyFilledInInfo } from "./use-my-previously-filled-in-info";
import {
  AddressCacheKey,
  setLastKnownAddress,
} from "./address-sync-cache";

export interface AddressFormFragmentProps {
  addressFormType: ReactCheckout2022_AddressFormTypes;
  title: string;

  formRef: React44ms.Api.TypedRef<
    ShippingAddressFormExpects | BillingAddressFormExpects
  >;
  formRef_isBillingAndShippingTheSame: React44ms.Api.TypedRef<CheckboxInput2023Expects>;

  shouldRestrictToCountry?: ACountry;
}
export const AddressFormFragment: React.FunctionComponent<
  AddressFormFragmentProps
> = (props) => {
  const { data: customerInformation } = useCustomerInformationQuery();
  const { data: checkoutRegionsData } = useDataSourceCheckoutRegionsQuery();

  const {
    handleLastSelectedSavedAddressIdChange:
      deliveryDetailsUserExperience_asController_handleLastSelectedSavedAddressIdChange,
  } = useDeliveryDetailsUserExperience_asController();

  const [state, locate] = React44ms.React.useForm<
    ShippingAddressFormExpects | BillingAddressFormExpects,
    ShippingAddressFormExpects
  >(props.formRef);
  const savedAddress =
    (state as unknown as ShippingAddressFormExpects)?.savedAddress || {};

  const _onlyHereToListenToTheFormChanges = React44ms.React.useForm(
    locate.savedAddress.typedRef
  );

  useRememberMyDetails(props.addressFormType, state, locate);

  const cacheKey: AddressCacheKey = React.useMemo(
    () =>
      props.addressFormType === ReactCheckout2022_AddressFormTypes.shipping
        ? "shipping"
        : "billing",
    [props.addressFormType]
  );

  React.useEffect(() => {
    const addressManual = state?.addressManual;
    setLastKnownAddress(cacheKey, {
      firstName: state?.firstName?.value,
      lastName: state?.lastName?.value,
      mobile: state?.mobile?.value,
      addressLine1: addressManual?.addressLine1?.value,
      addressLine2: addressManual?.addressLine2?.value,
      suburb: addressManual?.suburb?.value,
      postcode: addressManual?.postcode?.value,
      state: addressManual?.state?.value,
      country: addressManual?.country?.value,
    });
  }, [
    cacheKey,
    state?.firstName?.value,
    state?.lastName?.value,
    state?.mobile?.value,
    state?.addressManual?.addressLine1?.value,
    state?.addressManual?.addressLine2?.value,
    state?.addressManual?.suburb?.value,
    state?.addressManual?.postcode?.value,
    state?.addressManual?.state?.value,
    state?.addressManual?.country?.value,
  ]);

  const [doesTheUserPreferAutocomplete, setDoesTheUserPreferAutocomplete] =
    React.useState(true);

  enum AddressFormPresentationType {
    Autocomplete = "Autocomplete",
    Manual = "Manual",
    LoggedInCustomerSavedAddresses = "LoggedInCustomerSavedAddresses",
  }
  const addressFormType: AddressFormPresentationType = React.useMemo(() => {
    if (savedAddress.enabled) {
      return AddressFormPresentationType.LoggedInCustomerSavedAddresses;
    } else if (doesTheUserPreferAutocomplete) {
      return AddressFormPresentationType.Autocomplete;
    }
    return AddressFormPresentationType.Manual;
  }, [
    savedAddress,
    savedAddress.enabled,
    doesTheUserPreferAutocomplete,
    props.addressFormType,
    customerInformation,
  ]);

  const presentOptionToSelectASavedAddress = React.useMemo(() => {
    let presentOptionToSelectASavedAddress = false;
    if (
      addressFormType !==
        AddressFormPresentationType.LoggedInCustomerSavedAddresses &&
      savedAddress.capable
    ) {
      presentOptionToSelectASavedAddress = true;
    }
    return presentOptionToSelectASavedAddress;
  }, [addressFormType, savedAddress.capable]);

  const stateOptions: SelectWithDropdownInput_Option[] = React.useMemo(
    () =>
      (checkoutRegionsData?.available_regions || []).map((region) => ({
        id: region.code,
        presentableLabel: region.name,
      })),
    [checkoutRegionsData]
  );

  const countryOptions: ManualAddressFormFragmentProps["countryOptions"] =
    React.useMemo(
      () =>
        [
          {
            id: ACountry.Australia,
            presentableLabel: "Australia",
            presentableLabelForRestriction: "Australian",
          },
          {
            id: ACountry.NewZealand,
            presentableLabel: "New Zealand",
            presentableLabelForRestriction: "New Zealand",
          },
        ].filter(
          ({ id }) =>
            id !== ACountry.Singapore &&
            (!props.shouldRestrictToCountry ||
              id === props.shouldRestrictToCountry)
        ),
      [props.shouldRestrictToCountry]
    );

  const restrictionAdditionsIfNecessary = React.useMemo(() => {
    return props.shouldRestrictToCountry
      ? `- ${
          countryOptions.find(({ id }) => id === props.shouldRestrictToCountry)
            ?.presentableLabelForRestriction
        } address only`
      : "";
  }, [props.shouldRestrictToCountry, countryOptions]);

  const stateInputName = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(
      locate.addressManual.state.typedRef
    );
  }, [locate.addressManual.state.typedRef]);
  const countryInputName = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(
      locate.addressManual.country.typedRef
    );
  }, [locate.addressManual.country.typedRef]);
  const inputNames = React.useMemo(
    () => ({
      firstName: React44ms.React.Presentation.elements.nameFromRef(
        locate.firstName.typedRef
      ),
      lastName: React44ms.React.Presentation.elements.nameFromRef(
        locate.lastName.typedRef
      ),
      mobile: React44ms.React.Presentation.elements.nameFromRef(
        locate.mobile.typedRef
      ),
      addressLine1: React44ms.React.Presentation.elements.nameFromRef(
        locate.addressManual.addressLine1.typedRef
      ),
      addressLine2: React44ms.React.Presentation.elements.nameFromRef(
        locate.addressManual.addressLine2.typedRef
      ),
      suburb: React44ms.React.Presentation.elements.nameFromRef(
        locate.addressManual.suburb.typedRef
      ),
      postcode: React44ms.React.Presentation.elements.nameFromRef(
        locate.addressManual.postcode.typedRef
      ),
    }),
    [
      locate.addressManual.addressLine1.typedRef,
      locate.addressManual.addressLine2.typedRef,
      locate.addressManual.postcode.typedRef,
      locate.addressManual.suburb.typedRef,
      locate.firstName.typedRef,
      locate.lastName.typedRef,
      locate.mobile.typedRef,
    ]
  );

  const isManualAddressForm = React.useMemo(
    () => addressFormType === AddressFormPresentationType.Manual,
    [addressFormType]
  );
  const mobileValidationCountryCode = React.useMemo(() => {
    if (isManualAddressForm) {
      return state?.addressManual?.country?.value || props.shouldRestrictToCountry;
    }

    return props.shouldRestrictToCountry;
  }, [
    isManualAddressForm,
    state?.addressManual?.country?.value,
    props.shouldRestrictToCountry,
  ]);

  React.useEffect(() => {
    if (!isManualAddressForm) {
      return;
    }
    if (!stateInputName && !countryInputName) {
      return;
    }
    let attempts = 0;
    const maxAttempts = 30;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (stateInputName) {
        const stateLabel =
          document
            .querySelector(
              `.named-${stateInputName} .pretend-this-is-an-input`
            )
            ?.textContent?.trim() || "";
        if (stateLabel && stateLabel.length > 0) {
          const matchingState = stateOptions.find(
            (option) => option.presentableLabel === stateLabel
          );
          if (matchingState) {
            setLastKnownAddress(cacheKey, { state: `${matchingState.id}` });
          }
        }
      }
      if (countryInputName) {
        const countryLabel =
          document
            .querySelector(
              `.named-${countryInputName} .pretend-this-is-an-input`
            )
            ?.textContent?.trim() || "";
        if (countryLabel && countryLabel.length > 0) {
          const matchingCountry = countryOptions.find(
            (option) => option.presentableLabel === countryLabel
          );
          if (matchingCountry) {
            setLastKnownAddress(cacheKey, {
              country: `${matchingCountry.id}`,
            });
          }
        }
      }
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 200);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    addressFormType,
    cacheKey,
    countryInputName,
    countryOptions,
    isManualAddressForm,
    stateInputName,
    stateOptions,
  ]);

  React.useEffect(() => {
    if (!isManualAddressForm) {
      return;
    }
    const handlers: Array<{
      input: HTMLInputElement;
      handler: () => void;
    }> = [];
    let observer: MutationObserver | null = null;
    let attempts = 0;
    const maxAttempts = 30;
    const attachInput = (
      name: string | undefined,
      field: keyof Parameters<typeof setLastKnownAddress>[1]
    ) => {
      if (!name) {
        return;
      }
      const input = document.querySelector(
        `input[name="${name}"]`
      ) as HTMLInputElement | null;
      if (!input) {
        return;
      }
      const handler = () => {
        if (!input.value) {
          return;
        }
        setLastKnownAddress(cacheKey, { [field]: input.value });
      };
      input.addEventListener("input", handler);
      input.addEventListener("change", handler);
      handlers.push({ input, handler });
    };
    const tryAttach = () => {
      attachInput(inputNames.firstName, "firstName");
      attachInput(inputNames.lastName, "lastName");
      attachInput(inputNames.mobile, "mobile");
      attachInput(inputNames.addressLine1, "addressLine1");
      attachInput(inputNames.addressLine2, "addressLine2");
      attachInput(inputNames.suburb, "suburb");
      attachInput(inputNames.postcode, "postcode");
      return handlers.length > 0;
    };
    if (!tryAttach()) {
      const intervalId = window.setInterval(() => {
        attempts += 1;
        if (tryAttach() || attempts >= maxAttempts) {
          window.clearInterval(intervalId);
        }
      }, 200);
      const observerTarget = document.querySelector(".delivery-details") || document.body;
      observer = new MutationObserver(() => {
        if (tryAttach() && observer) {
          observer.disconnect();
          observer = null;
        }
      });
      observer.observe(observerTarget, { childList: true, subtree: true });
      return () => {
        window.clearInterval(intervalId);
        if (observer) {
          observer.disconnect();
        }
        handlers.forEach(({ input, handler }) => {
          input.removeEventListener("input", handler);
          input.removeEventListener("change", handler);
        });
      };
    }
    return () => {
      if (observer) {
        observer.disconnect();
      }
      handlers.forEach(({ input, handler }) => {
        input.removeEventListener("input", handler);
        input.removeEventListener("change", handler);
      });
    };
  }, [
    addressFormType,
    cacheKey,
    isManualAddressForm,
    inputNames.addressLine1,
    inputNames.addressLine2,
    inputNames.firstName,
    inputNames.lastName,
    inputNames.mobile,
    inputNames.postcode,
    inputNames.suburb,
  ]);

  React.useEffect(() => {
    if (savedAddress.capable) {
      const turn_the_saved_address_on = true;
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        [
          locate.savedAddress,
          React44ms.Api.YieldablePort(
            React44ms.Api.Convenience.element.name<SavedAddressExpects>(
              "enabled"
            )
          ),
        ],
        turn_the_saved_address_on
      );
    }
  }, [savedAddress.capable]);

  const handleUserWantsToEnterManualAddress_fromAutocomplete: React.MouseEventHandler<HTMLAnchorElement> =
    React.useCallback((event) => {
      event.preventDefault();

      setDoesTheUserPreferAutocomplete(false);
    }, []);

  const handleGoBackToAddressLookup_fromManualAddress: React.MouseEventHandler<HTMLAnchorElement> =
    React.useCallback((event) => {
      event.preventDefault();

      setDoesTheUserPreferAutocomplete(true);
    }, []);

  const handleEnterANewAddressInstead: React.MouseEventHandler<HTMLAnchorElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();

        const a_value_meaning__turn_the_saved_address_off = false;
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          [
            locate.savedAddress,
            React44ms.Api.YieldablePort(
              React44ms.Api.Convenience.element.name<SavedAddressExpects>(
                "enabled"
              )
            ),
          ],
          a_value_meaning__turn_the_saved_address_off
        );
      },
      [locate.savedAddress]
    );

  const handleSelectASavedAddressInstead: React.MouseEventHandler<HTMLAnchorElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();

        const a_value_meaning__turn_the_saved_address_on = true;
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          [
            locate.savedAddress,
            React44ms.Api.YieldablePort(
              React44ms.Api.Convenience.element.name<SavedAddressExpects>(
                "enabled"
              )
            ),
          ],
          a_value_meaning__turn_the_saved_address_on
        );
      },
      [locate.savedAddress]
    );

  const handleLastSelectedSavedAddressIdChange: SavedAddressFormFragmentProps["handleSelectAddress"] =
    React.useCallback(
      (value) => {
        const newAddressIdToSelect = value.id;

        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.savedAddress,
          newAddressIdToSelect
        );

        deliveryDetailsUserExperience_asController_handleLastSelectedSavedAddressIdChange(
          newAddressIdToSelect
        );
      },
      [
        locate,
        deliveryDetailsUserExperience_asController_handleLastSelectedSavedAddressIdChange,
      ]
    );

  return (
    <React.Fragment>
      <section
        className={`address ${props.addressFormType} AddressFormPresentationType-${addressFormType}`}
      >
        <h2>{`${props.title}`}</h2>

        {presentOptionToSelectASavedAddress && (
          <a
            className="select-an-existing-address"
            onClick={handleSelectASavedAddressInstead}
            href="javascript:void(0)"
          >
            <span>{`Select an existing address`}</span>
          </a>
        )}

        {/* Names */}
        {[
          AddressFormPresentationType.Autocomplete,
          AddressFormPresentationType.Manual,
        ].includes(addressFormType) && (
          <div className="names">
            <AnyNameFormLogic formRef={locate.firstName.typedRef} required />
            <SingleLineTextInput2023
              title="First Name"
              formRef={locate.firstName.typedRef}
              charLength={24}
            />

            <AnyNameFormLogic formRef={locate.lastName.typedRef} required />
            <SingleLineTextInput2023
              title="Last Name"
              formRef={locate.lastName.typedRef}
              charLength={25}
            />
          </div>
        )}

        {[ReactCheckout2022_AddressFormTypes.shipping].includes(
          props.addressFormType
        ) && (
          <React.Fragment>
            <SavedAddressFormLogic
              ref={locate.savedAddress.typedRef}
              yieldToRefAddressManual={locate.addressManual.typedRef}
              shouldRestrictToCountry={props.shouldRestrictToCountry}
            />
            {[
              AddressFormPresentationType.LoggedInCustomerSavedAddresses,
            ].includes(addressFormType) && (
              <React.Fragment>
                <SavedAddressFormFragment
                  addressFormType={props.addressFormType}
                  formRef={locate.savedAddress.typedRef}
                  formRef_isBillingAndShippingTheSame={
                    props.addressFormType ===
                    ReactCheckout2022_AddressFormTypes.shipping
                      ? props.formRef_isBillingAndShippingTheSame
                      : undefined
                  }
                  handleSelectAddress={handleLastSelectedSavedAddressIdChange}
                  handleEnterANewAddressInstead={handleEnterANewAddressInstead}
                  shouldRestrictToCountry={props.shouldRestrictToCountry}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        <AddressAutocompleteFormLogic
          ref={locate.addressAutocomplete.typedRef}
          yieldToRefAddressManual={locate.addressManual.typedRef}
          required={
            addressFormType === AddressFormPresentationType.Autocomplete
          }
          shouldRestrictToCountry={props.shouldRestrictToCountry}
        />
        {addressFormType === AddressFormPresentationType.Autocomplete && (
          <React.Fragment>
            <AutocompleteInput2023
              title={`${props.addressFormType[0].toUpperCase()}${props.addressFormType.slice(
                1
              )} Address ${restrictionAdditionsIfNecessary}`}
              formRef={locate.addressAutocomplete.typedRef}
            >
              <p className="cant-find-your-address-enter-it-manually">
                <span>{`Can’t find your address? `}</span>
                <a
                  href="javascript:void(0)"
                  onClick={handleUserWantsToEnterManualAddress_fromAutocomplete}
                >{`Enter it manually`}</a>
              </p>
            </AutocompleteInput2023>
          </React.Fragment>
        )}

        <ManualAddressFormLogic
          ref={locate.addressManual.typedRef}
          required
          shouldRestrictToCountry={props.shouldRestrictToCountry}
        />
        {addressFormType === AddressFormPresentationType.Manual && (
          <React.Fragment>
            <ManualAddressFormFragment
              addressFormType={props.addressFormType}
              formRef={locate.addressManual.typedRef}
              handleGoBackToAddressLookup={
                handleGoBackToAddressLookup_fromManualAddress
              }
              countryOptions={countryOptions}
              restrictionAdditionsIfNecessary={restrictionAdditionsIfNecessary}
            />
          </React.Fragment>
        )}

        {/* Mobile */}
        {[
          AddressFormPresentationType.Autocomplete,
          AddressFormPresentationType.Manual,
        ].includes(addressFormType) && (
          <React.Fragment>
            <MobileFormLogic2023
              formRef={locate.mobile.typedRef}
              required
              countryCode={mobileValidationCountryCode}
            />
            <SingleLineTextInput2023
              formRef={locate.mobile.typedRef}
              title="Mobile Number"
              notice={
                props.addressFormType ===
                ReactCheckout2022_AddressFormTypes.shipping
                  ? `We will use this to send delivery notifications about your purchase.`
                  : undefined
              }
            />
          </React.Fragment>
        )}
      </section>
    </React.Fragment>
  );
};

const useRememberMyDetails = (
  addressFormType: ReactCheckout2022_AddressFormTypes,
  formState: ReturnType<
    typeof React44ms.React.useForm<
      ShippingAddressFormExpects | BillingAddressFormExpects,
      ShippingAddressFormExpects
    >
  >[0],
  locate: ReturnType<
    typeof React44ms.React.useForm<
      ShippingAddressFormExpects | BillingAddressFormExpects,
      ShippingAddressFormExpects
    >
  >[1]
) => {
  const customerInformation = useCustomerInformationQuery();
  const myBagDataSource = useMyBagDataSourceIntersection();
  const shippingSessionStorageData =
    useDataMutableSourceShippingSessionStorageData();

  const dataMutableSourceShippingSessionStorageData_asController =
    useDataMutableSourceShippingSessionStorageData_asController();

  const { shouldMigrateDeliveryDetails, handleMigrationComplete } =
    useMyPreviouslyFilledInInfo();
  const handleMigration = React.useCallback(() => {
    if (
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLInputElement
    ) {
      return;
    }
    const { data: customerInformationData } = customerInformation || {};
    if (customerInformationData) {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        locate.firstName,
        customerInformationData.firstname || ""
      );
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        locate.lastName,
        customerInformationData.lastname || ""
      );
    }

    const { latest: myBagResponse } = myBagDataSource;
    if (myBagResponse) {
      const addresses =
        addressFormType === ReactCheckout2022_AddressFormTypes.shipping
          ? myBagResponse.shipping_addresses
          : myBagResponse.billing_address
          ? [myBagResponse.billing_address]
          : [];

      const thisCartAddress =
        addresses && addresses.length > 0 ? addresses[0] : null;
      if (thisCartAddress) {
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.firstName,
          thisCartAddress.firstname
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.lastName,
          thisCartAddress.lastname
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.mobile,
          thisCartAddress.telephone
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.country,
          thisCartAddress.country.code.toLowerCase()
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.addressLine1,
          thisCartAddress.street?.[0] || ""
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.addressLine2,
          thisCartAddress.street?.[1] || ""
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.suburb,
          thisCartAddress.city
        );
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.postcode,
          thisCartAddress.postcode
        );

        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate.addressManual.state,
          thisCartAddress.region?.code
        );
      }
    }

    const sessionData = shippingSessionStorageData;
    const lastValidAutocompleteAddress =
      addressFormType === ReactCheckout2022_AddressFormTypes.shipping
        ? sessionData.lastFormattedAutocompletedAddress_forShipping
        : sessionData.lastFormattedAutocompletedAddress_forBilling;

    if (
      lastValidAutocompleteAddress &&
      (lastValidAutocompleteAddress.currentValue?.length || 0) > 0
    ) {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        [
          locate.addressAutocomplete,
          React44ms.Api.YieldablePort(
            React44ms.Api.Convenience.element.name<AutocompleteInputFormController>(
              "handleSelectASuggestion"
            )
          ),
        ],
        lastValidAutocompleteAddress
      );
    }
  }, [
    addressFormType,
    myBagDataSource.latest?.shipping_addresses,
    myBagDataSource.latest?.billing_address,
    customerInformation.data?.firstname,
    customerInformation.data?.lastname,
    shippingSessionStorageData,
    locate.firstName,
    locate.lastName,
    locate.mobile,
    locate.addressManual.country,
    locate.addressManual.addressLine1,
    locate.addressManual.addressLine2,
    locate.addressManual.suburb,
    locate.addressManual.state,
    locate.addressManual.postcode,
    locate.addressAutocomplete,
  ]);
  React.useEffect(() => {
    if (shouldMigrateDeliveryDetails) {
      handleMigration();
      handleMigrationComplete();
    }
  }, [shouldMigrateDeliveryDetails, handleMigration, handleMigrationComplete]);

  React.useEffect(() => {
    if (
      formState?.addressAutocomplete?.currentValue &&
      !!formState?.addressAutocomplete?.hasBeenSelectedFromTheAutocomplete
    ) {
      if (addressFormType === ReactCheckout2022_AddressFormTypes.shipping) {
        dataMutableSourceShippingSessionStorageData_asController.handleLastFormattedAutocompletedAddressChange_forShipping(
          formState.addressAutocomplete
        );
      } else {
        dataMutableSourceShippingSessionStorageData_asController.handleLastFormattedAutocompletedAddressChange_forBilling(
          formState.addressAutocomplete
        );
      }
    }
  }, [
    formState?.addressAutocomplete?.currentValue,
    formState?.addressAutocomplete?.hasBeenSelectedFromTheAutocomplete,
    addressFormType,
    dataMutableSourceShippingSessionStorageData_asController,
  ]);
};
